import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BackRow } from "./shared";
import { SlotPicker, SlotsLoading, useConsultSlots } from "./SlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BookingScreen({ submissionId, assessmentToken, prefill, onDone, onBack }) {
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", phone: "", preferred_time: "",
    channel: "phone", consent_to_contact: false, marketing_opt_in: false,
    ...(prefill || {}),
  });
  const [busy, setBusy] = useState(false);
  const [slotStart, setSlotStart] = useState(null);
  const [slotsKey, setSlotsKey] = useState(0);
  const { live, slots } = useConsultSlots(slotsKey);

  const submit = async () => {
    if (live && !slotStart) return toast.error("Please pick a time.");
    if (live === false && form.preferred_time.trim().length < 2) return toast.error("Please tell us a time that works.");
    if (!form.consent_to_contact) {
      toast.error("Please check the appointment-contact consent box so Jonathan can confirm.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/next-move/bookings`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form, slot_start: live ? slotStart : null,
          submission_id: submissionId || null, assessment_token: assessmentToken || null,
        }),
      });
      if (r.status === 409 || r.status === 503) {
        toast.error((await r.json()).detail);
        setSlotStart(null);
        setSlotsKey((k) => k + 1);
        return;
      }
      if (!r.ok) throw new Error(await r.text());
      toast.success(live ? "You're booked — check your email for the calendar invite." : "Booking received — Jonathan will reach out to confirm the exact time.");
      onDone(live ? slots.find((x) => x.start === slotStart)?.label : null);
    } catch (e) {
      toast.error("Something went wrong — please call 435-535-7000 to book directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-7 md:p-10">
      {onBack && <BackRow onBack={onBack} progress="Talk It Through" />}
      <h2 className="font-heading text-2xl md:text-3xl text-cvc-navy leading-snug">A free 20-minute conversation with Jonathan.</h2>
      <p className="font-body text-cvc-ink-soft mt-3 text-sm leading-relaxed">
        No obligation. {live === false ? "Jonathan will reach out to confirm the exact time at your preferred window." : "Pick an open time below. It goes straight onto Jonathan's calendar."}
      </p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Your name"       value={form.name}         onChange={(v) => setForm({...form, name: v})}         testId="nm-book-name" />
        <Field label="Business name"   value={form.business_name} onChange={(v) => setForm({...form, business_name: v})} testId="nm-book-business" />
        <Field label="Email"           value={form.email}         onChange={(v) => setForm({...form, email: v})}         testId="nm-book-email" type="email" />
        <Field label="Phone"           value={form.phone}         onChange={(v) => setForm({...form, phone: v})}         testId="nm-book-phone" type="tel" />
        {live === null && <SlotsLoading />}
        {live && <SlotPicker slots={slots} value={slotStart} onChange={setSlotStart} />}
        {live === false && (
          <Field label="Preferred time"  value={form.preferred_time} onChange={(v) => setForm({...form, preferred_time: v})} testId="nm-book-time" placeholder="e.g. Thursday afternoon" wide />
        )}
      </div>
      <div className="mt-5">
        <Label className="font-body text-sm text-cvc-ink-soft">How would you like to meet?</Label>
        <div className="mt-2 flex gap-2">
          {["phone", "in_person"].map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`nm-book-channel-${v}`}
              onClick={() => setForm({ ...form, channel: v })}
              className={`px-4 py-2 rounded-full border font-body text-sm transition-colors
                ${form.channel === v ? "bg-cvc-navy text-cvc-cream border-cvc-navy" : "bg-white border-cvc-border hover:bg-cvc-parchment"}`}
            >
              {v === "phone" ? "By phone" : "In person (Logan)"}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 flex items-start gap-3">
        <Checkbox id="nm-book-consent" data-testid="nm-book-consent"
          checked={form.consent_to_contact}
          onCheckedChange={(v) => setForm({...form, consent_to_contact: !!v})}
          className="mt-1" />
        <Label htmlFor="nm-book-consent" className="font-body text-sm text-cvc-ink leading-snug">
          Yes, Jonathan can call/email me about this appointment.
        </Label>
      </div>
      <div className="mt-3 flex items-start gap-3">
        <Checkbox id="nm-book-marketing" data-testid="nm-book-marketing"
          checked={form.marketing_opt_in}
          onCheckedChange={(v) => setForm({...form, marketing_opt_in: !!v})}
          className="mt-1" />
        <Label htmlFor="nm-book-marketing" className="font-body text-xs text-cvc-ink-soft leading-snug">
          Optional: send me occasional business-scaling tips from Cache Valley Consulting. (You can unsubscribe anytime.)
        </Label>
      </div>
      <div className="mt-6">
        <Button data-testid="nm-book-submit" onClick={submit} disabled={busy}
          className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-7 py-5 h-auto font-body disabled:opacity-60">
          {busy ? "Sending..." : "Book this conversation"}
        </Button>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, testId, type = "text", placeholder, wide = false }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="font-body text-sm text-cvc-ink-soft">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} className="mt-1 bg-white" />
    </div>
  );
}
